import { useEffect, useRef, useState } from "react";
import { Facility, FleetVehicle, validPosition } from "./fleetData";
import { fleetAtBase, fleetDestination } from "./fleetRoute";
import type { FeatureCollection, LineString } from "geojson";

interface Candidate { id: string; key: string; start: {lat:number;lng:number}; end: {lat:number;lng:number}; base: boolean; }
interface Estimate { text: string; retryAfter: number; geometry?: LineString; distance?: number; duration?: number; }
type BaseRoutesCallback=(routes:FeatureCollection<LineString>,unavailable:number)=>void;
export interface FleetLabelFields { speed: string; distance: string; time: string; }

export function fleetLabelFields(vehicle: FleetVehicle, facilities: Facility[], estimate?: string): FleetLabelFields {
  const value = vehicle.unreachable || vehicle.telemetry?.metricsStale ? undefined : vehicle.speed ?? vehicle.telemetry?.speed;
  const speed = typeof value === "number" && Number.isFinite(value) ? Math.round(value)+" km/h" : "— km/h";
  if(vehicle.unreachable) return {speed,distance:"Last known",time:"— min"};
  if(fleetAtBase(vehicle,facilities)) return {speed,distance:"At base",time:"— min"};
  const parts = estimate?.match(/^(\d+(?:\.\d+)? km) · (~\d+ min)$/);
  if(parts) return {speed,distance:parts[1],time:parts[2]};
  const pending = !estimate && Boolean(fleetDestination(vehicle,facilities));
  return {speed,distance:pending?"… km":"— km",time:pending?"… min":"— min"};
}

export function estimateCandidates(vehicles: FleetVehicle[], facilities: Facility[]): Candidate[] {
  return vehicles.flatMap(vehicle => {
    const start = validPosition(vehicle.lat, vehicle.lng), end = fleetDestination(vehicle, facilities);
    if (!start || !end || vehicle.unreachable || fleetAtBase(vehicle, facilities)) return [];
    return [{ id: vehicle.id, key: JSON.stringify([start.lng,start.lat,end.lng,end.lat]), start, end, base:end.kind==="base" }];
  });
}

export default function useFleetEstimates(vehicles: FleetVehicle[], facilities: Facility[], enabled: boolean,onBaseRoutes?:BaseRoutesCallback) {
  const cache = useRef(new Map<string, Estimate>());
  const latestRoutes=useRef(new Map<string,{end:string;estimate:Estimate;key:string}>());
  const routesCallback=useRef(onBaseRoutes);routesCallback.current=onBaseRoutes;
  const [values, setValues] = useState<Record<string,string>>({});
  const [retryTick,setRetryTick]=useState(0);
  const candidatesKey = JSON.stringify(estimateCandidates(vehicles, facilities));
  useEffect(() => {
    if (!enabled) return;
    const candidates: Candidate[] = JSON.parse(candidatesKey);
    const controller = new AbortController();
    let active = true, cursor = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const publishRoutes=()=>{
      if(!active || !routesCallback.current) return;
      let unavailable=0;
      const features:FeatureCollection<LineString>["features"]=[];
      candidates.filter(candidate=>candidate.base).forEach(candidate=>{
        const exact=cache.current.get(candidate.key);
        const previous=latestRoutes.current.get(candidate.id);
        const retained=previous?.end===JSON.stringify([candidate.end.lng,candidate.end.lat])?previous:undefined;
        const entry=exact?.geometry?{estimate:exact,key:candidate.key}:retained;
        if(entry?.estimate.geometry) features.push({type:"Feature",geometry:entry.estimate.geometry,properties:{id:candidate.id,key:entry.key,distance:entry.estimate.distance,duration:entry.estimate.duration}});
        else if(exact?.text==="Distance / time unavailable") unavailable++;
      });
      routesCallback.current({type:"FeatureCollection",features},unavailable);
    };
    const next: Record<string,string> = {};
    candidates.forEach(candidate => { const cached = cache.current.get(candidate.key); if(cached) next[candidate.id] = cached.text; });
    setValues(next);
    publishRoutes();
    const routingUrl = (process.env.REACT_APP_FLEET_ROUTING_URL || "https://router.project-osrm.org").replace(/\/$/, "");
    const load = async () => {
      while (active && cursor < candidates.length) {
        const candidate = candidates[cursor++];
        const cached = cache.current.get(candidate.key);
        if (cached && cached.retryAfter > Date.now() && (!candidate.base || cached.geometry || cached.text==="Distance / time unavailable")) continue;
        const request = new AbortController();
        const abort = () => request.abort();
        controller.signal.addEventListener("abort", abort, {once:true});
        const timeout = setTimeout(abort,15000);
        try {
          const { start, end } = candidate;
          const query=candidate.base?"overview=full&geometries=geojson":"overview=false";
          const response = await fetch(`${routingUrl}/route/v1/driving/${start.lng},${start.lat};${end.lng},${end.lat}?${query}`,{signal:request.signal});
          if(!response.ok) throw new Error("Estimate unavailable");
          const data = await response.json(), route = data.routes?.[0];
          if(data.code !== "Ok" || !Number.isFinite(route?.distance) || route.distance < 0 || !Number.isFinite(route?.duration) || route.duration < 0) throw new Error("Invalid estimate");
          if(candidate.base && (route.geometry?.type!=="LineString" || !Array.isArray(route.geometry.coordinates) || route.geometry.coordinates.length<2 || route.geometry.coordinates.some((point:number[])=>!Array.isArray(point) || !validPosition(point[1],point[0])))) throw new Error("Invalid base route");
          if(!active) return;
          const text = (route.distance/1000).toFixed(1)+" km · ~"+Math.ceil(route.duration/60)+" min";
          if(cache.current.size >= 200) cache.current.clear();
          const estimate:Estimate={text,retryAfter:Infinity,distance:route.distance,duration:route.duration,geometry:candidate.base?route.geometry:undefined};
          cache.current.set(candidate.key,estimate);
          if(candidate.base) latestRoutes.current.set(candidate.id,{end:JSON.stringify([end.lng,end.lat]),estimate,key:candidate.key});
          setValues(current => ({...current,[candidate.id]:text}));
          publishRoutes();
        } catch {
          if(!active) return;
          cache.current.set(candidate.key,{text:"Distance / time unavailable",retryAfter:Date.now()+30000});
          latestRoutes.current.delete(candidate.id);
          setValues(current => ({...current,[candidate.id]:"Distance / time unavailable"}));
          publishRoutes();
        } finally {
          clearTimeout(timeout); controller.signal.removeEventListener("abort",abort);
        }
      }
    };
    void Promise.all(Array.from({length:Math.min(4,candidates.length)},load)).then(()=>{
      if(!active) return;
      const retries=candidates.map(candidate=>cache.current.get(candidate.key)?.retryAfter).filter((time):time is number=>time!==undefined && Number.isFinite(time));
      if(retries.length) retryTimer=setTimeout(()=>setRetryTick(tick=>tick+1),Math.max(1000,Math.min(...retries)-Date.now()));
    });
    return () => { active=false;controller.abort();if(retryTimer) clearTimeout(retryTimer); };
  },[candidatesKey,enabled,retryTick]);
  return values;
}

export function fleetMetricLabel(vehicle: FleetVehicle, facilities: Facility[], estimate?: string): string {
  const speed = vehicle.unreachable || vehicle.telemetry?.metricsStale ? undefined : vehicle.speed ?? vehicle.telemetry?.speed;
  const speedText = typeof speed === "number" && Number.isFinite(speed) ? Math.round(speed)+" km/h" : "— km/h";
  if(vehicle.unreachable) return "Last known · "+speedText;
  if(fleetAtBase(vehicle,facilities)) return speedText+" · At base";
  return speedText+" · "+(estimate || (fleetDestination(vehicle,facilities) ? "Estimating route…" : "No route target"));
}
